import React, { FC, Fragment, useCallback, useState, startTransition } from 'react';
import classNames from 'classnames';

import { FileTypes, FileFormats, ClickEventType, FileTypesAlias } from 'home/view/check-feasibility/comp-generator/common-interface';

import ProgressBar from './ProgressBar';

import { validateFile } from './utils';
import uploadIcon from '../../assests/upload.svg';
import './index.scss';

interface FileUploadCbProps {
  e: React.ChangeEvent<HTMLInputElement>;
  fileList: FileList;
  error: string;
  setFileError: React.Dispatch<React.SetStateAction<string | React.ReactNode>>;
  setDownloadPercentage: React.Dispatch<React.SetStateAction<number>>;
  isDropEvent: boolean;
}

/**
 * onFileUpload: (
 * fileList -> valid uploaded files (inbuilt file size validation),
 * error -> custom handling like error notification,
 * setFileError -> set custom error on specific scenarios like when file is uploaded and errorSheet gets generated,
 * setDownloadPercentage -> set downloadedPercentage from xhr request; optional i.e. if not done loader won't render
 */
interface Props {
  format: Array<FileTypes>;
  size: number;
  multiple?: boolean;
  abortController?: AbortController;
  onFileUpload?: (fileData: FileUploadCbProps) => void;
  limit?: number;
  files?: Array<any>;
  onFileItemClick?: Function;
  onDeleteFile?: Function;
  onDragLeave?: Function;
  onDragOver?: Function;
  disabled?: boolean;
}

export const FileUpload: FC<Props> = ({
  format,
  size,
  multiple = true,
  abortController = new AbortController(),
  onFileUpload,
  limit,
  files = [],
  onFileItemClick,
  onDeleteFile,
  onDragLeave,
  onDragOver,
  disabled = false
}) => {
  const [fileError, setFileError] = useState<string | React.ReactNode>('');
  const [downloadPercentage, setDownloadPercentage] = useState<number>(0);

  const getFileAcceptAttributeValue = (format: Array<FileTypes>): string => {
    const acceptedFormats: Array<FileTypes> = [];

    format.forEach((item: FileTypes) => {
      const currentType = FileFormats[item] as any;
      acceptedFormats.push(currentType);
    });

    return acceptedFormats?.join(',');
  };

  const getFileFormatsAlias = (format: Array<FileTypes>): string => {
    const acceptedFormatsAlias: Array<FileTypes> = [];

    format.forEach((item: FileTypes) => {
      const currentType = FileTypesAlias[item] as any;
      acceptedFormatsAlias.push(currentType);
    });

    return acceptedFormatsAlias.join(' ');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | any, config?: any) => {
    let isDropEvent = false;

    if (config) {
      isDropEvent = config?.isDropEvent;
    }

    e?.preventDefault?.();

    let fileList = e?.target?.files;

    if (isDropEvent) {
      fileList = e?.dataTransfer?.files;
    }

    const defaultFileLength = files?.length ?? 0;
    const uploadedFileLength = fileList?.length ?? 0;
    const totalFileListLength = defaultFileLength + uploadedFileLength;

    if (!fileList?.length) {
      return false;
    }

    if (limit && uploadedFileLength > limit) {
      return false;
    }

    if (limit && totalFileListLength > limit) {
      return false;
    }

    const error = validateFile(fileList, size);
    setFileError(error);

    startTransition(() => {
      onFileUpload?.({ e, fileList, error, setFileError, setDownloadPercentage, isDropEvent });
    });
  };

  const handleProgressCancelClick = (e: ClickEventType): void => {
    e.preventDefault();

    // cancel on going network request
    abortController?.abort();

    // reset file upload section
    setFileError('');
    setDownloadPercentage(0);
  };

  const renderUploadedFiles = () => {
    return files.map(({ name, size, type, documentName }, index) => {
      return (
        <div className="custom_file_content" key={name + index}>
          <a className="name_text" role="button" onClick={onFileItemClick?.bind(null, documentName, type)}>
            {name}
          </a>
          <span className="cross_icon" onClick={onDeleteFile?.bind(null, name, documentName)}></span>
        </div>
      );
    });
  };

  const onDrop = (event: any) => {
    event.stopPropagation();

    handleChange(event, { isDropEvent: true });
  };
  const handleDragLeave = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    onDragLeave?.(event);
  };
  const handleDragOver = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    onDragOver?.(event);
  };

  const renderProgressBar = useCallback(() => {
    if (!downloadPercentage) {
      return null;
    }

    return <ProgressBar percent={downloadPercentage} onCancelConfirmClick={handleProgressCancelClick} />;
  }, [downloadPercentage]);

  const renderUploadSection = useCallback(() => {
    if (downloadPercentage) {
      return null;
    }

    return (
      <div
        className={classNames({
          'upload-details': true,
          disabled: disabled || files?.length === limit || false
        })}
        onDrop={(event) => onDrop(event)}
        onDragOver={(event) => handleDragOver(event)}
        onDragLeave={(event) => handleDragLeave(event)}
      >
        <div className="icon-wrapper">
          <img src={uploadIcon} />
        </div>
        <input
          type="file"
          data-testid="file-container"
          className="file-box"
          accept={getFileAcceptAttributeValue(format)}
          onChange={handleChange}
          multiple={multiple}
          disabled={disabled || files?.length === limit || false}
        />
        <div className="upload-text">
          Drag & drop a file here or <span>Browse</span>
        </div>
        <div className="sub-text">
          Supported file formats - {getFileFormatsAlias(format)} (Max file size is {size} Mb)
        </div>
      </div>
    );
  }, [format, size, downloadPercentage, files]);

  const uploadWrapperClass = classNames({
    'upload-section': true,
    'upload-error': fileError
  });

  const fileListClassname = classNames({
    'file-name-container': true,
    disabled: disabled
  });

  return (
    <Fragment>
      <div className={uploadWrapperClass}>
        {renderProgressBar()}
        {renderUploadSection()}
      </div>

      {/**
       * File error section
       */}
      <div className="error-text">{fileError}</div>
      <div className={fileListClassname}>{renderUploadedFiles()}</div>
    </Fragment>
  );
};

export default FileUpload;
